// libs
import { useMemo, useCallback, useState, VFC } from 'react'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalCloseButton,
  LoadingButton,
  RenderModalProps
} from '@panwds/react-ui'
import { Form, FormLayout } from '@panwds/react-form'
import { actions, Tsg } from '@sparky/framework'
import { useFormContext } from 'react-hook-form'
import _ from 'lodash'

// services
import TenancyService from '../../../activation/services/TenancyService'

// components
import ErrorBanner from '../../../activation/components/common/ErrorBanner'
import { FormInput } from '../../../tenancy/components/licenses/AddTenant'

// utils
import StringTemplate from '../../../common/utils/StringTemplate'

// constants
import {
  TSG_NONE
} from '../../constants'
import {
  ACTIVATION_UI_ERROR_CODES
} from '../../../activation/ErrorCodes'
import {
  ROOT_NODE_ID,
  TENANT_ADD,
} from '../../../tenancy/constants/TenancyConstants'
import {
  SUPER_ROOT_ID
} from '../../../activation/constants'
import {
  BUTTON_DONE,
  BUTTON_CANCEL,
} from '../../../common/constants/LabelConstants'
import {
  TITLE_CREATE_TENANT,
  TITLE_EDIT_TENANT
} from '../../../tenancy/constants/LabelConstants'

const Footer = (props: any) => {
  const { isLoading } = props
  const { formState, getValues } = useFormContext()
  const { isValid } = formState

  const display_name = getValues('display_name')

  const disabled = useMemo(() => {
    return !isValid || !Boolean(display_name)
  }, [display_name, isValid])

  return (
    <>
      <LoadingButton
        appearance='primary'
        disabled={disabled}
        dataResult={{ loading: isLoading, success: false }}
        type='submit'
      >{BUTTON_DONE}</LoadingButton>
      <ModalCloseButton>{BUTTON_CANCEL}</ModalCloseButton>
    </>
  )
}

const TenantCreationModal: VFC<RenderModalProps & {
  selectedTsg: Tsg,
  onCreatedTsg: (tsg: Tsg) => void,
  isEdit?: boolean,
  sgKeyBy?: { [id: string]: Tsg }
}> = (props) => {

  const {
    isOpen,
    closeModal,
    selectedTsg,
    onCreatedTsg,
    isEdit,
    sgKeyBy
  } = props

  const [isLoading, setLoading] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>('')

  /**
   * APPORTAL-9448, comment below code as we don't block the behavior anymore
  */
  // const [isButtonDisabled, setButtonDisabled] = useState<boolean>(false)
  // useEffect(() => {
  //   const checkEnable = async () => {
  //     if (selectedTsg.id === SUPER_ROOT_ID) {
  //       return
  //     }
  //     setLoading(true)
  //     const tsgInstances = await loadTsgInstances(selectedTsg.id).catch((err) => {
  //       setButtonDisabled(true)
  //       setErrorMsg(ACTIVATION_UI_ERROR_CODES.CREATE_TENANT_FAILED)
  //     })
  //     if (tsgInstances?.has(APP_ID_PRISMA_ACCESS_PANORAMA)) {
  //       setButtonDisabled(true)
  //       setErrorMsg(ACTIVATION_UI_ERROR_CODES.CANNOT_CREATE_TSG)
  //     }
  //     setLoading(false)
  //   }
  //   checkEnable()
  // }, [selectedTsg.id])

  const clearErrorMessage = useCallback(() => {
    setErrorMsg('')
  }, [])

  const editFormValues = useMemo(() => {
    const parent_id = selectedTsg?.parent_id as string
    const parent_contact = sgKeyBy?.[parent_id]?.support_contact || ''
    const custom_contact = selectedTsg?.support_contact === parent_contact ? '' : selectedTsg?.support_contact
    const initial = selectedTsg?.isRoot || (Boolean(custom_contact) && custom_contact !== parent_contact) ? 'custom' : 'parent'
    const support_contact = {
      parent: parent_contact,
      custom: custom_contact,
      initial
    }
    return {
      display_name: selectedTsg?.display_name,
      vertical: { value: selectedTsg?.vertical },
      support_contact
    }
  }, [selectedTsg, sgKeyBy])

  /**
   * Get Form values for add prefilled
   * @type {{support_contact: *}}
   */
  const addFormValues = useMemo(() => {
    const parent_contact = selectedTsg?.support_contact || ''
    const support_contact = {
      parent: parent_contact,
      custom: '',
      initial: 'parent'
    }
    return { support_contact }
  }, [selectedTsg?.support_contact])

  const formValues = useMemo(() => {
    if (isEdit) {
      return editFormValues
    }
    return addFormValues
  }, [addFormValues, editFormValues, isEdit])

  const handleCreate = useCallback(async ({ display_name, vertical, support_contact }) => {
    const payload = {
      display_name,
      parent_id: selectedTsg.id === SUPER_ROOT_ID ? undefined : selectedTsg.id,
      vertical: vertical?.value,
      support_contact: support_contact || undefined,
    }
    const { data } = await TenancyService.createTenant.post(payload, selectedTsg.id === SUPER_ROOT_ID ? TSG_NONE.id : selectedTsg.id)
    return data
  }, [selectedTsg.id])

  const handleEdit = useCallback(async ({ display_name, vertical, support_contact }) => {
    const payload = _.omitBy({
      display_name,
      vertical: vertical?.value,
      support_contact: support_contact || undefined,
      parent_id: selectedTsg.parent_id !== ROOT_NODE_ID ? selectedTsg.parent_id : undefined
    }, _.isNil)
    const { data } = await TenancyService.listTenant.put(payload, selectedTsg?.id)
    return data
  }, [selectedTsg?.id, selectedTsg.parent_id])

  const onSubmit = useCallback(async (values) => {
    const { display_name, support_contact, vertical } = values
    try {
      setLoading(true)
      if (isEdit) {
        const tsg = await handleEdit({ display_name, vertical, support_contact })
        await onCreatedTsg(tsg)
      }
      else {
        const tsg = await handleCreate({ display_name, vertical, support_contact })
        await onCreatedTsg(tsg)
      }
      await actions.loadTsgTree()
      setLoading(false)
      closeModal()
    }
    catch (error: any) {
      setLoading(false)
      setErrorMsg(error?.error?.message ?? error?.message ?? ACTIVATION_UI_ERROR_CODES.CREATE_TENANT_FAILED)
    }
  }, [isEdit, closeModal, handleEdit, handleCreate, onCreatedTsg])

  const title = useMemo(() => {
    if (isEdit) {
      return StringTemplate(TITLE_EDIT_TENANT, [selectedTsg.display_name])
    }
    return StringTemplate(TITLE_CREATE_TENANT, [selectedTsg.display_name])
  }, [isEdit, selectedTsg.display_name])

  return (
    <Modal
      size='md'
      confirmClose={false}
      onClose={closeModal}
      isOpen={isOpen}
    >
      <Form onSubmit={onSubmit}>
        <ModalHeader title={title} enableClose/>
        {errorMsg && <ErrorBanner errorCode={errorMsg} callback={clearErrorMessage} bannerProps={{ type: 'inline' }} />}
        <ModalBody addClassName='cm-overflow-auto'>
          <div className='cm-flex cm-flex-col cm-space-y-2 cm-text-sm'>
            <FormLayout>
              <FormInput
                formValues={formValues}
                selectedTreeNode={selectedTsg}
                actionType={TENANT_ADD}
                loading={isLoading}
              />
            </FormLayout>
          </div>
        </ModalBody>
        <ModalFooter>
          <Footer isLoading={isLoading}/>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default TenantCreationModal
